import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import LabShell from "./LabShell";
import StyleContext from "../contexts/StyleContext";

const INITIAL_CRAFT = {
  x: 150,
  y: 390,
  vx: 4,
  vy: -2,
  angle: -0.35,
  fuel: 100,
  state: "approach"
};

const STATION = {x: 754, y: 250};
const stars = Array.from({length: 54}, (_, index) => ({
  x: (index * 137 + 47) % 900,
  y: (index * 83 + 29) % 520,
  radius: index % 7 === 0 ? 1.8 : .8
}));

const angleError = angle => {
  let normalized = angle % (Math.PI * 2);
  if (normalized > Math.PI) normalized -= Math.PI * 2;
  if (normalized < -Math.PI) normalized += Math.PI * 2;
  return normalized;
};

export default function DockingSimulator() {
  const {isDark} = useContext(StyleContext);
  const canvasRef = useRef(null);
  const craftRef = useRef({...INITIAL_CRAFT});
  const frameRef = useRef();
  const lastTimeRef = useRef();
  const missionStateRef = useRef("approach");
  const rangeValueRef = useRef(null);
  const speedValueRef = useRef(null);
  const attitudeValueRef = useRef(null);
  const fuelValueRef = useRef(null);
  const [missionState, setMissionState] = useState("approach");

  const reset = useCallback(() => {
    craftRef.current = {...INITIAL_CRAFT};
    missionStateRef.current = "approach";
    lastTimeRef.current = undefined;
    setMissionState("approach");
  }, []);

  const fire = useCallback(command => {
    const craft = craftRef.current;
    if (craft.state !== "approach" || craft.fuel <= 0) return;

    if (command === "rotate-left") craft.angle -= .12;
    if (command === "rotate-right") craft.angle += .12;

    let force = 0;
    let direction = craft.angle;
    if (command === "forward") force = 9;
    if (command === "reverse") force = -6;
    if (command === "strafe-left") { force = 5; direction -= Math.PI / 2; }
    if (command === "strafe-right") { force = 5; direction += Math.PI / 2; }
    if (force) {
      craft.vx += Math.cos(direction) * force;
      craft.vy += Math.sin(direction) * force;
    }
    craft.fuel = Math.max(
      0,
      craft.fuel - (command.indexOf("rotate") === 0 ? 2 : 4)
    );
    if (craft.fuel === 0) {
      craft.state = "fuel-empty";
      craft.vx = 0;
      craft.vy = 0;
      missionStateRef.current = "fuel-empty";
      if (fuelValueRef.current) fuelValueRef.current.textContent = "0%";
      if (speedValueRef.current) speedValueRef.current.textContent = "0.0 m/s";
      setMissionState("fuel-empty");
    }
  }, []);

  useEffect(() => {
    const onKeyDown = event => {
      const controls = {
        ArrowUp: "forward", w: "forward", W: "forward",
        ArrowDown: "reverse", s: "reverse", S: "reverse",
        ArrowLeft: "rotate-left", a: "rotate-left", A: "rotate-left",
        ArrowRight: "rotate-right", d: "rotate-right", D: "rotate-right",
        q: "strafe-left", Q: "strafe-left", e: "strafe-right", E: "strafe-right"
      };
      if (controls[event.key]) {
        event.preventDefault();
        fire(controls[event.key]);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fire]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    let telemetryTick = 0;

    const draw = time => {
      const craft = craftRef.current;
      const dt = Math.min(((time - (lastTimeRef.current || time)) / 1000), .035);
      lastTimeRef.current = time;

      if (craft.state === "approach") {
        craft.x += craft.vx * dt;
        craft.y += craft.vy * dt;

        if (craft.x < 22 || craft.x > 878 || craft.y < 22 || craft.y > 498) {
          craft.state = "boundary";
          craft.vx = 0;
          craft.vy = 0;
        }

        const horizontalRange = STATION.x - craft.x;
        const verticalRange = Math.abs(STATION.y - craft.y);
        const speed = Math.hypot(craft.vx, craft.vy);
        const touchesStation = craft.x > STATION.x - 44 && craft.y > 116 && craft.y < 384;
        const insideDockingPort = verticalRange < 27 && horizontalRange < 44;

        if (craft.state === "approach" && touchesStation) {
          if (
            insideDockingPort &&
            speed < 18 &&
            Math.abs(angleError(craft.angle)) < .32
          ) {
            craft.state = "docked";
            craft.x = STATION.x - 32;
            craft.y = STATION.y;
            craft.vx = 0;
            craft.vy = 0;
          } else {
            craft.state = "collision";
            craft.vx = 0;
            craft.vy = 0;
          }
        }
      }

      const gradient = context.createLinearGradient(0, 0, 900, 520);
      gradient.addColorStop(0, isDark ? "#020713" : "#edf5ff");
      gradient.addColorStop(1, isDark ? "#081431" : "#dfeaff");
      context.fillStyle = gradient;
      context.fillRect(0, 0, 900, 520);

      context.fillStyle = isDark ? "rgba(210,235,255,.78)" : "rgba(24,55,105,.55)";
      stars.forEach(star => {
        context.beginPath();
        context.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        context.fill();
      });

      context.strokeStyle = isDark ? "rgba(83,120,235,.18)" : "rgba(42,82,151,.2)";
      context.lineWidth = 1;
      context.beginPath();
      context.ellipse(510, 275, 360, 118, -.16, 0, Math.PI * 2);
      context.stroke();

      context.save();
      context.translate(STATION.x, STATION.y);
      context.fillStyle = isDark ? "#dcecff" : "#19345d";
      context.fillRect(0, -82, 54, 164);
      context.fillStyle = isDark ? "#3458aa" : "#7698d8";
      context.fillRect(54, -118, 105, 72);
      context.fillRect(54, 46, 105, 72);
      context.fillStyle = "#071126";
      context.fillRect(-9, -23, 18, 46);
      context.strokeStyle = "#73e7ff";
      context.lineWidth = 3;
      context.strokeRect(-13, -28, 26, 56);
      context.restore();

      context.save();
      context.translate(craft.x, craft.y);
      context.rotate(craft.angle);
      if (craft.state === "collision") {
        context.shadowColor = "#ff5d58";
        context.shadowBlur = 24;
      } else {
        context.shadowColor = "#6edfff";
        context.shadowBlur = 16;
      }
      context.fillStyle = craft.state === "collision" ? "#ff6b62" : "#f5fbff";
      context.beginPath();
      context.moveTo(29, 0);
      context.lineTo(-19, -15);
      context.lineTo(-11, 0);
      context.lineTo(-19, 15);
      context.closePath();
      context.fill();
      context.shadowBlur = 0;
      context.fillStyle = "#3c64c8";
      context.fillRect(-15, -25, 18, 12);
      context.fillRect(-15, 13, 18, 12);
      context.fillStyle = "#122550";
      context.beginPath();
      context.arc(5, 0, 6, 0, Math.PI * 2);
      context.fill();
      context.restore();

      telemetryTick += dt;
      if (craft.state !== missionStateRef.current) {
        missionStateRef.current = craft.state;
        setMissionState(craft.state);
      }

      if (telemetryTick > .1) {
        telemetryTick = 0;
        const range = Math.max(
          0,
          Math.hypot(STATION.x - craft.x, STATION.y - craft.y) - 32
        );
        const speed = Math.hypot(craft.vx, craft.vy);
        if (rangeValueRef.current) rangeValueRef.current.textContent = `${Math.round(range)} m`;
        if (speedValueRef.current) speedValueRef.current.textContent = `${speed.toFixed(1)} m/s`;
        if (attitudeValueRef.current) attitudeValueRef.current.textContent = `${Math.round(angleError(craft.angle) * 180 / Math.PI)}°`;
        if (fuelValueRef.current) fuelValueRef.current.textContent = `${Math.round(craft.fuel)}%`;
      }
      frameRef.current = window.requestAnimationFrame(draw);
    };

    frameRef.current = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(frameRef.current);
  }, [isDark]);

  const stateCopy = {
    approach: "Approach active",
    docked: "Docking confirmed",
    collision: "Impact detected",
    boundary: "Vehicle lost",
    "fuel-empty": "Propellant depleted"
  };

  const outcomeCopy = {
    docked: {
      kicker: "MISSION COMPLETE",
      title: "DOCKING CONFIRMED",
      detail: "Soft capture achieved inside the docking envelope."
    },
    collision: {
      kicker: "MISSION FAILED",
      title: "IMPACT DETECTED",
      detail: "The spacecraft contacted station structure outside the docking envelope."
    },
    boundary: {
      kicker: "MISSION FAILED",
      title: "VEHICLE LOST",
      detail: "The spacecraft crossed the simulation boundary."
    },
    "fuel-empty": {
      kicker: "MISSION FAILED",
      title: "PROPELLANT 0%",
      detail: "All maneuvering propellant has been depleted."
    }
  };

  return (
    <LabShell
      eyebrow="MISSION LAB / 01"
      title="Docking Simulator"
      intro="Bring your vehicle to the station with low relative velocity and a level attitude. Gentle inputs win this one."
    >
      <section className="simulator-layout">
        <div className="simulator-stage">
          <div className={`sim-status ${missionState}`}>{stateCopy[missionState]}</div>
          <canvas ref={canvasRef} width="900" height="520" aria-label="Interactive spacecraft docking simulator" />
          {missionState !== "approach" && (
            <div
              className={`sim-outcome ${missionState === "docked" ? "is-success" : "is-failure"}`}
              role="alert"
            >
              <span>{outcomeCopy[missionState].kicker}</span>
              <strong>{outcomeCopy[missionState].title}</strong>
              <p>{outcomeCopy[missionState].detail}</p>
              <button onClick={reset}>Try again</button>
            </div>
          )}
        </div>

        <aside className="simulator-panel">
          <div className="telemetry-heading">
            <span>LIVE TELEMETRY</span>
            <button onClick={reset}>Reset mission</button>
          </div>
          <div className="telemetry-grid">
            <div><span>RANGE</span><strong ref={rangeValueRef}>620 m</strong></div>
            <div><span>SPEED</span><strong ref={speedValueRef}>4.5 m/s</strong></div>
            <div><span>ATTITUDE</span><strong ref={attitudeValueRef}>-20°</strong></div>
            <div><span>PROPELLANT</span><strong ref={fuelValueRef}>100%</strong></div>
          </div>
          <div className="sim-objective">
            <span>DOCKING ENVELOPE</span>
            <p>Contact below 18 m/s with attitude inside ±18°.</p>
          </div>
          <div className="thruster-controls" aria-label="Spacecraft thruster controls">
            <button disabled={missionState !== "approach"} onClick={() => fire("rotate-left")}><kbd>A</kbd> Rotate left</button>
            <button disabled={missionState !== "approach"} onClick={() => fire("forward")}><kbd>W</kbd> Forward</button>
            <button disabled={missionState !== "approach"} onClick={() => fire("rotate-right")}><kbd>D</kbd> Rotate right</button>
            <button disabled={missionState !== "approach"} onClick={() => fire("strafe-left")}><kbd>Q</kbd> Strafe left</button>
            <button disabled={missionState !== "approach"} onClick={() => fire("reverse")}><kbd>S</kbd> Reverse</button>
            <button disabled={missionState !== "approach"} onClick={() => fire("strafe-right")}><kbd>E</kbd> Strafe right</button>
          </div>
        </aside>
      </section>
    </LabShell>
  );
}
